import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const GA_ID = "G-99JZK8ZLZD";
const CONSENT_KEY = "ceogabriel.cookie_consent.v1";

// Network timeouts (ms)
const TRACK_TIMEOUT_MS = 2500;
const STATS_TIMEOUT_MS = 4000;
const INSTAGRAM_TIMEOUT_MS = 8000;

export const AFFILIATE_URL =
  "https://ro.puprime.com/forex-trading-account/?affid=MjMyMTU4Njk=";
export const TELEGRAM_URL = "https://t.me/ceosjournal";
export const TEAM_TELEGRAM_URL = "https://t.me/payoutsbyceoteam";

/* ---------- Cookie consent ---------- */
export function getCookieConsent() {
  try {
    return localStorage.getItem(CONSENT_KEY);
  } catch (e) {
    return null;
  }
}

export function setCookieConsent(value) {
  try {
    localStorage.setItem(CONSENT_KEY, value);
  } catch (e) {
    // localStorage unavailable (private mode / quota) — ignore
  }
  applyConsent(value);
  // Notify any listener (e.g. OpenAI Ads Pixel loader) that consent changed.
  try {
    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("ceogabriel:consentchange", { detail: value })
      );
    }
  } catch (e) {
    // silent
  }
}

export function applyConsent(value) {
  if (typeof window === "undefined") return;
  const allow = value === "all";
  // Google's recommended runtime flag to disable GA.
  window[`ga-disable-${GA_ID}`] = !allow;
  window.__analyticsAllowed = allow;
}

// Apply current consent (or block analytics by default until user chooses).
if (typeof window !== "undefined") {
  applyConsent(getCookieConsent() || "pending");
}

/** Fire a GA4 custom event (gated on consent). */
export function gaEvent(name, params = {}) {
  try {
    if (typeof window === "undefined") return;
    if (!window.__analyticsAllowed) return;
    if (typeof window.gtag === "function") {
      window.gtag("event", name, params);
    }
  } catch (e) {
    // silent
  }
}

export async function trackClick(ctaId, event = null) {
  // Note: server-side click tracking is anonymous (no PII, no cookie, just
  // aggregate counts) — used only to power the social-proof counters. It
  // intentionally does NOT depend on cookie consent. GA tracking remains
  // gated separately inside gaEvent().
  try {
    await axios.post(
      `${API}/track-click`,
      {
        cta_id: ctaId,
        event,
        referrer: typeof document !== "undefined" ? document.referrer : null,
        path: typeof window !== "undefined" ? window.location.pathname : null,
      },
      { timeout: TRACK_TIMEOUT_MS }
    );
  } catch (e) {
    // silent
  }
}

export function handleAffiliateClick(ctaId) {
  trackClick(ctaId, "affiliate_click");
  gaEvent("affiliate_click", { cta_id: ctaId });
  window.open(AFFILIATE_URL, "_blank", "noopener,noreferrer");
}

export function handleTelegramClick(source = "card") {
  trackClick(source, "telegram_click");
  gaEvent("telegram_click", { source });
  window.open(TELEGRAM_URL, "_blank", "noopener,noreferrer");
}

/**
 * Fire an OpenAI Pixel custom measurement event. Safe wrapper: if the
 * pixel SDK is not available (e.g. blocked, not yet loaded, network
 * error), this becomes a silent no-op and MUST NOT prevent navigation.
 */
function oaiqCustom(customEventName) {
  try {
    if (typeof window === "undefined") return;
    if (typeof window.oaiq !== "function") return;
    window.oaiq(
      "measure",
      "custom",
      { type: "custom" },
      { custom_event_name: customEventName }
    );
  } catch (e) {
    // silent — tracking must never break UX
  }
}

/* Homepage — "CEO's Journal" card (opens https://t.me/ceosjournal). */
export function handleJournalClick(source = "hub_main") {
  trackClick(source, "ceos_journal_click");
  gaEvent("ceos_journal_click", { source });
  // Additional OpenAI Pixel measurement — does not replace GA4.
  oaiqCustom("ceo_journal_join");
  window.open(TELEGRAM_URL, "_blank", "noopener,noreferrer");
}

/* Homepage — "Payouts By CEO | Team" card (opens https://t.me/payoutsbyceoteam). */
export function handleTeamClick(source = "hub_main") {
  trackClick(source, "team_telegram_click");
  gaEvent("team_telegram_click", { source });
  // Additional OpenAI Pixel measurement — does not replace GA4.
  oaiqCustom("payouts_team_contact");
  window.open(TEAM_TELEGRAM_URL, "_blank", "noopener,noreferrer");
}

export async function fetchRecentCount(event, hours = 24) {
  try {
    const res = await axios.get(
      `${API}/track-stats/recent?event=${encodeURIComponent(event)}&hours=${hours}`,
      { timeout: STATS_TIMEOUT_MS }
    );
    return typeof res.data?.count === "number" ? res.data.count : null;
  } catch (e) {
    return null;
  }
}

export async function fetchInstagramPosts(limit = 5) {
  try {
    const res = await axios.get(`${API}/instagram/recent-posts?limit=${limit}`, {
      timeout: INSTAGRAM_TIMEOUT_MS,
    });
    return res.data;
  } catch (e) {
    return { configured: false, posts: [] };
  }
}

export async function fetchCommunityResults() {
  try {
    const res = await axios.get(`${API}/community/results`, {
      timeout: INSTAGRAM_TIMEOUT_MS,
    });
    const items = Array.isArray(res.data?.items) ? res.data.items : [];
    // Build absolute URLs against REACT_APP_BACKEND_URL so images load in both
    // preview and production without depending on the request host.
    return items.map((it) => ({
      id: it.id,
      thumb_url: `${BACKEND_URL}${it.thumb_path}`,
      full_url: `${BACKEND_URL}${it.full_path}`,
      created_time: it.created_time || null,
    }));
  } catch (e) {
    return [];
  }
}
